import { useRef } from 'react';

import { ACTIVITIES_PAGE_OBJECTIVES } from 'apps/web/constants/activities';
import ACTIVITIES_FORM_KEYS from 'apps/web/constants/forms/activities/formKeys';
import useAutosizeTextArea from 'apps/web/hooks/useAutoSizeTextArea';
import processInput from 'apps/web/utils/formInput';

import {
  DIFFICULTY,
  MATERIALS,
  TIME,
  LC_PAGE_SUBJECT_CHAR_LIMIT,
  LP_PAGE_OBJECTIVE_CHAR_LIMIT,
} from '@lessonPlanner/constants';

import { SelectField, TextAreaField, TextField } from '../form-fields';
import {
  FrameworkDropdown,
  GradeLevelDropdown,
  StateDropdown,
} from '../Select';

export default function AmericanActivities({
  watchedValues,
  errors,
  setValue,
  triggerValidation,
}: {
  watchedValues;
  errors;
  setValue;
  triggerValidation;
}) {
  const {
    gradeLevel,
    subject,
    objective,
    strategies,
    difficulty,
    materials,
    time,
  } = ACTIVITIES_FORM_KEYS;

  const objectiveRef = useRef<HTMLTextAreaElement>(null);
  const strategiesRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(objectiveRef.current, watchedValues[objective]);
  useAutosizeTextArea(strategiesRef.current, watchedValues[strategies]);

  const updateFieldValue = async (fieldName: string, value: unknown) => {
    setValue(fieldName, value);
  };

  const renderSelectField = (
    id: string,
    label: string,
    options: string[],
    errorMessage: string,
  ) => (
    <SelectField
      className="col-span-full"
      error={!!errors[id]}
      errorMessage={!!errors[id] && errorMessage}
      id={id}
      label={label}
      name={id}
      updateFn={updateFieldValue}
      validateFn={triggerValidation}
      value={watchedValues[id]}
      onBlur={() => triggerValidation(id)}
      onChange={(value) => updateFieldValue(id, value)}
    >
      {options.map((option, index) => (
        <option key={index} value={option}>
          {option}
        </option>
      ))}
    </SelectField>
  );

  return (
    <>
      <FrameworkDropdown
        error={errors.curriculumFramework}
        label="Curriculum Framework"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.curriculumFramework}
        errorMessage={
          !!errors.curriculumFramework && 'Please select a Framework'
        }
      />

      <GradeLevelDropdown
        error={!!errors[gradeLevel]}
        errorMessage={!!errors[gradeLevel] && 'Please select the Grade Level'}
        label="Grade Level / Educational Stage"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues[gradeLevel]}
      />

      <TextField
        className="col-span-full"
        errorMessage={!!errors[subject] && 'Please enter the Subject'}
        id={subject}
        label="Subject / Discipline"
        maxLength={LC_PAGE_SUBJECT_CHAR_LIMIT}
        name={subject}
        placeholder="Input the subject for this activity"
        value={watchedValues[subject]}
        isMaxLengthReached={
          watchedValues[subject]?.length >= LC_PAGE_SUBJECT_CHAR_LIMIT
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            subject,
            watchedValues[subject]?.trim() || '',
          );
        }}
        onChange={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            subject,
            e.target.value,
          );
        }}
      />

      <StateDropdown
        error={errors.specificStandards}
        label="State Standard"
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.specificStandards}
        errorMessage={
          !!errors.specificStandards && 'Please select a State Standard'
        }
      />

      <TextAreaField
        showSubLabel
        className="col-span-full"
        error={!!errors[objective]}
        errorMessage={!!errors[objective] && 'Please enter the Objectives'}
        id={objective}
        label="Objective"
        maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
        name={objective}
        placeholder="Input the objectives for this activity"
        rows={2}
        subLabel={ACTIVITIES_PAGE_OBJECTIVES}
        textAreaRef={objectiveRef}
        type={objective}
        value={watchedValues[objective]}
        isMaxLengthReached={
          watchedValues[objective]?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            objective,
            watchedValues[objective]?.trim() || '',
          );
        }}
        onChange={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            objective,
            e.target.value,
          );
        }}
      />

      <TextAreaField
        className="col-span-full"
        error={!!errors[strategies]}
        errorMessage={!!errors[strategies] && 'Please enter the Strategies'}
        id={strategies}
        label="Strategies"
        name={strategies}
        placeholder="Instructional strategies used"
        rows={2}
        textAreaRef={strategiesRef}
        type={strategies}
        value={watchedValues[strategies]}
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            strategies,
            watchedValues[strategies]?.trim() || '',
          );
        }}
        onChange={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            strategies,
            e.target.value,
          );
        }}
      />

      {renderSelectField(
        difficulty,
        'Difficulty/Complexity',
        DIFFICULTY,
        'Please select the Difficulty/Complexity',
      )}
      {renderSelectField(
        materials,
        'Materials',
        MATERIALS,
        'Please select the Material',
      )}
      {renderSelectField(
        time,
        'Duration of the Activity',
        TIME,
        'Please select the Time',
      )}
    </>
  );
}
